import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
    chartCard: {
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        padding: theme.spacing(3),
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.6s ease',
    },
    chartTitle: {
        marginBottom: theme.spacing(3),
        fontSize: '2rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    noDataText: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
        fontSize: '1rem',
    },
}));

function ResponseBreakdownChart({ loading, data }) {
    const classes = useStyles();
    const theme = useTheme();
    const chartRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [animationCount, setAnimationCount] = useState(0);
    const [chartKey, setChartKey] = useState(0);

    // Intersection Observer to detect when chart is in viewport
    useEffect(() => {
        // Only set up observer if chart hasn't animated 2 times yet
        if (animationCount >= 2) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !loading && data && animationCount < 2) {
                    setIsVisible(true);
                    setAnimationCount(prev => prev + 1);
                    // Force chart re-render to trigger animation
                    setChartKey(prev => prev + 1);
                } else if (!entry.isIntersecting) {
                    setIsVisible(false);
                }
            },
            {
                threshold: 0.3,
                rootMargin: '0px'
            }
        );

        if (chartRef.current) {
            observer.observe(chartRef.current);
        }

        return () => {
            if (chartRef.current) {
                observer.unobserve(chartRef.current);
            }
        };
    }, [loading, data, animationCount]);

    // Memoize chart data
    const chartData = useMemo(() => {
        if (!data || !data.response_breakdown) return null;

        const responseBreakdown = data.response_breakdown || [];
        const labels = responseBreakdown.map(item => {
            const text = item.reply_text || '';
            // Check if it's a JSON string (flow response)
            if (text.startsWith('{') && text.endsWith('}')) {
                try {
                    const parsed = JSON.parse(text);
                    // Remove flow_token and extract meaningful keys
                    const parts = [];
                    Object.keys(parsed).forEach(key => {
                        if (key === 'flow_token') return;
                        // Remove screen_N_ prefix and replace underscores with spaces
                        const cleanKey = key.replace(/^screen_\d+_/, '').replace(/_/g, ' ');
                        // Remove leading number prefix from value (e.g., "0_Courier..." -> "Courier...")
                        const cleanValue = String(parsed[key]).replace(/^\d+_/, '').replace(/_/g, ' ');
                        parts.push(`${cleanKey}: ${cleanValue}`);
                    });
                    return parts.join(' | ') || text;
                } catch (e) {
                    return text;
                }
            }
            return text;
        });
        const percentages = responseBreakdown.map(item => parseFloat(item.percentage));

        // Color palette
        const colors = [
            '#10b981', // green
            '#ef4444', // red
            '#f59e0b', // orange
            '#94a3b8', // gray
            '#3b82f6', // blue
            '#8b5cf6', // purple
            '#ec4899', // pink
            '#14b8a6', // teal
            '#f97316', // orange-red
            '#6366f1', // indigo
            '#a855f7', // purple-light
        ];

        const backgroundColors = colors.slice(0, responseBreakdown.length);

        return {
            labels: labels,
            datasets: [{
                data: percentages,
                backgroundColor: backgroundColors,
                borderWidth: 2,
                borderColor: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
                hoverOffset: 4
            }]
        };
    }, [data, theme.palette]);

    // Memoize chart options
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'bottom',
            align: 'center',
            labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 15,
                fontSize: 12,
                fontStyle: '500',
                fontColor: theme.palette.text.primary
            }
        },
        tooltips: {
            backgroundColor: theme.palette.type === 'dark'
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            titleFontColor: theme.palette.text.primary,
            bodyFontColor: theme.palette.text.secondary,
            borderColor: theme.palette.type === 'dark' ? '#334155' : '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 12,
            displayColors: true,
            bodySpacing: 6,
            titleSpacing: 8,
            caretPadding: 10,
            callbacks: {
                label: (tooltipItem, chartData) => {
                    const dataIndex = tooltipItem.index;
                    const label = chartData.labels[dataIndex] || '';
                    const value = chartData.datasets[0].data[dataIndex] || 0;
                    const count = data.response_breakdown[dataIndex]?.count || 0;
                    return [
                        `${label}`,
                        `Percentage: ${value}%`,
                        `Count: ${count.toLocaleString()}`
                    ];
                }
            }
        },
        animation: {
            duration: animationCount <= 2 && isVisible ? 2000 : 0,
            easing: 'easeInOutCubic',
            animateRotate: true,
            animateScale: true
        }
    }), [theme.palette, data, isVisible, animationCount]);

    // Show skeleton while loading
    if (loading) {
        return (
            <Paper className={classes.chartCard}>
                <Skeleton
                    variant="text"
                    width="50%"
                    height={35}
                    style={{ marginBottom: '24px' }}
                    animation="wave"
                />
                <div style={{ height: '350px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Skeleton
                        variant="circle"
                        width={280}
                        height={280}
                        animation="wave"
                    />
                </div>
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    {[1, 2, 3].map(item => (
                        <Skeleton
                            key={item}
                            variant="text"
                            width={100}
                            height={20}
                            animation="wave"
                        />
                    ))}
                </div>
            </Paper>
        );
    }

    // Show no data message
    if (!data || !data.response_breakdown || data.response_breakdown.length === 0) {
        return (
            <Paper className={classes.chartCard}>
                <Typography className={classes.noDataText}>
                    No response breakdown data available
                </Typography>
            </Paper>
        );
    }

    // Show chart with animation when data is loaded
    return (
        <Paper
            ref={chartRef}
            className={classes.chartCard}
            style={{
                opacity: animationCount >= 2 ? 1 : (isVisible ? 1 : 0.3),
                transform: animationCount >= 2 ? 'scale(1)' : (isVisible ? 'scale(1)' : 'scale(0.95)'),
            }}
        >
            <Typography className={classes.chartTitle}>
                Response Breakdown
            </Typography>
            <div style={{ height: '350px' }}>
                {chartData && (
                    <Doughnut
                        key={chartKey}
                        data={chartData}
                        options={chartOptions}
                    />
                )}
            </div>
        </Paper>
    );
}

export default ResponseBreakdownChart;